import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Plus } from "lucide-react";
import { API, useApp } from "../context/AppContext";
import ImageOrArt from "../components/ImageOrArt";
import GeoArt from "../components/GeoArt";
import { MaskedLines, Reveal, SectionHead } from "../components/Reveal";

const CATEGORIES = ["All", "Original Artwork", "Prints", "Apparel", "Accessories", "RISE Marketplace"];
const COLLECTIONS = ["All", "Field Faces Collection", "Peace Love Power Collection", "RISE Collection"];
const BADGES = ["New Arrival", "Collector Favorite", "Limited Edition"];

function ProductCard({ p, i }) {
  const { addToCart } = useApp();
  return (
    <Reveal delay={(i % 3) * 0.06}>
      <div className="group border border-border" data-testid={`product-card-${p.id}`}>
        <div className="relative overflow-hidden">
          <div className="transition-transform duration-700 group-hover:scale-105">
            <ImageOrArt image={p.image} seed={p.seed} className="w-full" />
          </div>
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {p.badges.map((b) => (
              <span key={b} className="font-mono-x text-[9px] uppercase tracking-widest px-2 py-1 bg-black/85 text-white border border-white/15 w-max">{b}</span>
            ))}
          </div>
          <button
            onClick={() => addToCart(p)}
            data-testid={`add-to-cart-${p.id}`}
            aria-label={`Add ${p.name} to cart`}
            className="absolute bottom-3 right-3 w-11 h-11 bg-[#FF1A1A] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 hover:bg-[#E60000]"
          >
            <Plus size={18} />
          </button>
        </div>
        <div className="p-5 border-t border-border">
          <p className="font-mono-x text-[10px] uppercase tracking-widest text-muted-foreground">{p.collection}</p>
          <h3 className="font-display font-bold uppercase text-sm mt-2 leading-tight">{p.name}</h3>
          <div className="flex justify-between items-center mt-3">
            <span className="font-mono-x text-sm text-[#FF1A1A]" data-testid={`product-price-${p.id}`}>${p.price.toLocaleString()}</span>
            <button onClick={() => addToCart(p)} className="text-[10px] font-display font-bold uppercase tracking-widest hover:text-[#FF1A1A] transition-colors" data-testid={`quick-add-${p.id}`}>
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function Collect() {
  const [products, setProducts] = useState([]);
  const [params, setParams] = useSearchParams();
  const cat = params.get("category") || "All";
  const coll = params.get("collection") || "All";
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/products`, { params: { ...(cat !== "All" ? { category: cat } : {}), ...(coll !== "All" ? { collection: coll } : {}) } })
      .then(({ data }) => setProducts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cat, coll]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "All") next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  const featured = useMemo(() => products.filter((p) => p.badges.some((b) => BADGES.includes(b))).slice(0, 3), [products]);

  return (
    <main className="pt-24 sm:pt-32" data-testid="collect-page">
      <section className="container-x section-y">
        <p className="eyebrow mb-6">The Shop</p>
        <MaskedLines lines={["Collect the Art.", "Wear the Story."]} className="h-display" />
        <Reveal delay={0.4}>
          <div className="mt-12 space-y-4" data-testid="shop-filters">
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter("category", c)}
                  data-testid={`shop-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 border transition-colors duration-300 ${cat === c ? "bg-[#FF1A1A] border-[#FF1A1A] text-white" : "border-border hover:border-[#FF1A1A] hover:text-[#FF1A1A]"}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {COLLECTIONS.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter("collection", c)}
                  data-testid={`shop-collection-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`font-mono-x text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors duration-300 ${coll === c ? "border-[#FF1A1A] text-[#FF1A1A]" : "border-border text-muted-foreground hover:border-[#FF1A1A] hover:text-[#FF1A1A]"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {cat === "All" && coll === "All" && featured.length > 0 && (
        <section className="border-t border-border" data-testid="featured-products">
          <div className="container-x section-y">
            <SectionHead eyebrow="Featured" title="New Arrivals & Collector Favorites" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {featured.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
            </div>
          </div>
        </section>
      )}

      <section className="border-t border-border">
        <div className="container-x section-y">
          <SectionHead eyebrow="Catalog" title={cat === "All" ? "Everything" : cat} />
          {loading ? (
            <p className="font-mono-x text-sm text-muted-foreground" data-testid="shop-loading">Loading products…</p>
          ) : products.length === 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="shop-empty">Nothing here yet — try another category.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="product-grid">
              {products.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
